import { useCallback } from "react";
import { copyTextToClipboard, supportsNativeShare, shareAnalysisViaNavigator, buildAnalysisEmailHref, openMailClient, resolvePublicBaseUrl } from "@/utils/share";
import type { useToast } from "@/hooks/use-toast";

interface UseAnalysisShareOptions {
  scriptName: string;
  formName: string;
  reviewLink: string | null;
  toast: ReturnType<typeof useToast>["toast"];
}

export const useAnalysisShare = ({ scriptName, formName, reviewLink, toast }: UseAnalysisShareOptions) => {
  const canUseNativeShare = supportsNativeShare();

  const analysisShareUrl = useCallback(() => {
    if (!reviewLink) return "";
    const resolvedBase = resolvePublicBaseUrl();
    const fallback = typeof window !== "undefined" && window.location ? window.location.origin : "";
    const base = resolvedBase || fallback;
    const params = new URLSearchParams({ analysis: reviewLink });
    return base ? `${base}/?${params.toString()}` : `/?${params.toString()}`;
  }, [reviewLink]);

  const handlePrint = useCallback(() => {
    if (typeof window === "undefined") return;
    window.print();
  }, []);

  const handleCopyShareLink = useCallback(async () => {
    const url = analysisShareUrl();
    if (!url) return;

    try {
      await copyTextToClipboard(url);
      toast({
        title: "Copied to clipboard",
        description: "Analysis link copied successfully.",
      });
    } catch (error) {
      console.error("Failed to copy analysis link", error);
      toast({
        title: "Copy failed",
        description: "We couldn't copy that link. Please try again.",
        variant: "destructive",
      });
    }
  }, [analysisShareUrl, toast]);

  const handleShare = useCallback(async () => {
    const url = analysisShareUrl();
    if (!url) return;

    if (!canUseNativeShare) {
      await handleCopyShareLink();
      return;
    }

    try {
      await shareAnalysisViaNavigator({
        movieName: scriptName,
        formName,
        url,
      });
    } catch (error: unknown) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      console.error("Analysis share failed", error);
      toast({
        title: "Share failed",
        description: "We couldn't open the share dialog. Try copying the link instead.",
        variant: "destructive",
      });
    }
  }, [analysisShareUrl, canUseNativeShare, formName, handleCopyShareLink, scriptName, toast]);

  const handleEmailShare = useCallback(() => {
    const url = analysisShareUrl();
    if (!url) return;

    const mailtoHref = buildAnalysisEmailHref({
      movieName: scriptName,
      formName,
      url,
    });

    openMailClient(mailtoHref);
  }, [analysisShareUrl, formName, scriptName]);

  return {
    canUseNativeShare,
    analysisShareUrl: analysisShareUrl(),
    handlePrint,
    handleCopyShareLink,
    handleShare,
    handleEmailShare,
  };
};
